import { useState, useCallback, useEffect, useRef, type MouseEvent as ReactMouseEvent } from "react";
import { useAuth } from "@/hooks/useAuth";
import { userLsKey } from "@/lib/userScopedStorage";
import DOMPurify from "dompurify";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { AiCoachAnalysis, Habit } from "@shared/schema";
import { 
  Bot, Target, ListOrdered, Heart, CheckSquare, ListTodo, FileText, Calendar, 
  Play, Loader2, Download, Eye, Trash2, CheckCircle, XCircle, Clock, 
  TrendingUp, Lightbulb, Brain, Sparkles, Flag, Trophy, RefreshCw, Compass, 
  MessageSquare, Palette, Search, Zap, AlertCircle, ChevronDown, ChevronRight,
  Star, Gift, Cake, CalendarHeart, CalendarPlus, Timer, Check, Save, Settings, BarChart3
} from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { format, addDays } from "date-fns";
import { ReportingContent } from "./ai-coach-reporting";

// Settings types and defaults
interface AICoachSettings {
  includeGoals: boolean;
  includeAffirmations: boolean;
  includeGratefuls: boolean;
  includeHabits: boolean;
  includeTasks: boolean;
  includeJournal: boolean;
  includeCalendar: boolean;
  includeImportantDates: boolean;
}

const defaultSettings: AICoachSettings = {
  includeGoals: true,
  includeAffirmations: true,
  includeGratefuls: true,
  includeHabits: true,
  includeTasks: true,
  includeJournal: true,
  includeCalendar: true,
  includeImportantDates: true,
};

const settingsItems = [
  { key: "includeGoals" as const, label: "My Goals", description: "Include your goals and progress in AI coaching", icon: Target, constraintsRoute: "/ai-coach/constraints/goals" },
  { key: "includeAffirmations" as const, label: "Affirmations", description: "Include your daily affirmations", icon: ListOrdered, constraintsRoute: "/ai-coach/constraints/affirmations" },
  { key: "includeGratefuls" as const, label: "Gratefuls", description: "Include your gratitude entries", icon: Heart, constraintsRoute: "/ai-coach/constraints/gratefuls" },
  { key: "includeHabits" as const, label: "Habits", description: "Include your habit tracking data", icon: CheckSquare, constraintsRoute: "/ai-coach/constraints/habits" },
  { key: "includeTasks" as const, label: "Tasks", description: "Include your tasks and to-dos", icon: ListTodo, constraintsRoute: "/ai-coach/constraints/tasks" },
  { key: "includeJournal" as const, label: "Journal", description: "Include your journal entries", icon: FileText, constraintsRoute: "/ai-coach/constraints/journal" },
  { key: "includeCalendar" as const, label: "Calendar", description: "Include your calendar events", icon: Calendar, constraintsRoute: "/ai-coach/constraints/calendar" },
  { key: "includeImportantDates" as const, label: "Important Dates", description: "Include birthdays, anniversaries, and special occasions", icon: Star, constraintsRoute: "/calendar" },
];

// Habit analysis types
interface HabitAnalysis {
  id: string;
  name: string;
  frequency: string;
  streak: number;
  completionRate: number;
  status: 'on-track' | 'needs-improvement';
  recommendation: string;
}

interface TimeBlockConfig {
  habitId: string;
  habitName: string;
  days: string[];
  time: string;
  duration: number;
}

interface TimeBlockPreview {
  habitName: string;
  events: Array<{
    date: string;
    time: string;
    duration: number;
  }>;
}

type DataSourceType = 'goals' | 'affirmations' | 'gratefuls' | 'habits' | 'tasks' | 'journal' | 'calendar' | 'importantDates';

interface ConstraintOption {
  key: string;
  label: string;
  icon: any;
}

interface DataSourceConfig {
  label: string;
  icon: any;
  constraints: ConstraintOption[];
}

const dataSourceConfigs: Record<DataSourceType, DataSourceConfig> = {
  goals: {
    label: "My Goals",
    icon: Target,
    constraints: [
      { key: "progressInsights", label: "Progress Insights", icon: TrendingUp },
      { key: "milestoneRecommendations", label: "Milestone Recommendations", icon: Flag },
      { key: "prioritizationTips", label: "Goal Prioritization", icon: ListOrdered },
      { key: "timelineAdjustments", label: "Timeline Adjustments", icon: Clock },
      { key: "successStrategies", label: "Success Strategies", icon: Trophy },
      { key: "obstacleIdentification", label: "Obstacle Identification", icon: Search },
      { key: "goalAlignment", label: "Goal Alignment Analysis", icon: Compass },
    ]
  },
  affirmations: {
    label: "Daily Affirmations",
    icon: ListOrdered,
    constraints: [
      { key: "affirmationEffectiveness", label: "Effectiveness Analysis", icon: TrendingUp },
      { key: "personalizationSuggestions", label: "Personalization Tips", icon: Sparkles },
      { key: "timingOptimization", label: "Timing Optimization", icon: Clock },
      { key: "themePatterns", label: "Theme Patterns", icon: Brain },
    ]
  },
  gratefuls: {
    label: "Gratitude Entries",
    icon: Heart,
    constraints: [
      { key: "gratitudePatterns", label: "Gratitude Patterns", icon: TrendingUp },
      { key: "emotionalWellbeing", label: "Emotional Wellbeing", icon: Heart },
      { key: "focusAreas", label: "Focus Areas", icon: Target },
      { key: "growthOpportunities", label: "Growth Opportunities", icon: Sparkles },
    ]
  },
  habits: {
    label: "My Habits",
    icon: CheckSquare,
    constraints: [
      { key: "streakAnalysis", label: "Streak Analysis", icon: TrendingUp },
      { key: "habitStacking", label: "Habit Stacking", icon: RefreshCw },
      { key: "triggerIdentification", label: "Trigger Identification", icon: Lightbulb },
      { key: "consistencyTips", label: "Consistency Tips", icon: CheckCircle },
      { key: "habitFormation", label: "Habit Formation", icon: Brain },
    ]
  },
  tasks: {
    label: "My Tasks",
    icon: ListTodo,
    constraints: [
      { key: "taskPrioritization", label: "Task Prioritization", icon: ListOrdered },
      { key: "productivityPatterns", label: "Productivity Patterns", icon: TrendingUp },
      { key: "timeManagement", label: "Time Management", icon: Clock },
      { key: "delegationSuggestions", label: "Delegation Suggestions", icon: Zap },
      { key: "completionStrategies", label: "Completion Strategies", icon: CheckCircle },
    ]
  },
  journal: {
    label: "Journal Entries",
    icon: FileText,
    constraints: [
      { key: "emotionalPatternAnalysis", label: "Emotional Pattern Analysis", icon: Brain },
      { key: "writingPrompts", label: "Writing Prompts", icon: MessageSquare },
      { key: "reflectionInsights", label: "Reflection Insights", icon: Lightbulb },
      { key: "themeExtraction", label: "Theme Extraction", icon: Palette },
      { key: "growthTracking", label: "Growth Tracking", icon: TrendingUp },
    ]
  },
  calendar: {
    label: "Calendar Events",
    icon: Calendar,
    constraints: [
      { key: "scheduleOptimization", label: "Schedule Optimization", icon: Clock },
      { key: "meetingPatterns", label: "Meeting Patterns", icon: TrendingUp },
      { key: "timeBlocking", label: "Time Blocking", icon: Target },
      { key: "workLifeBalance", label: "Work-Life Balance", icon: Heart },
      { key: "priorityAlignment", label: "Priority Alignment", icon: Compass },
    ]
  },
  importantDates: {
    label: "Important Dates",
    icon: Star,
    constraints: [
      { key: "upcomingEvents", label: "Upcoming Events", icon: CalendarHeart },
      { key: "giftSuggestions", label: "Gift Suggestions", icon: Gift },
      { key: "anniversaryReminders", label: "Anniversary Reminders", icon: Heart },
      { key: "birthdayReminders", label: "Birthday Reminders", icon: Cake },
    ]
  },
};

const allDataSources: DataSourceType[] = ['goals', 'affirmations', 'gratefuls', 'habits', 'tasks', 'journal', 'calendar', 'importantDates'];

type TimeframeType = '30days' | '60days' | '90days' | '6months' | '1year';

const timeframeOptions: { value: TimeframeType; label: string }[] = [
  { value: '30days', label: 'Next 30 days' },
  { value: '60days', label: 'Next 60 days' },
  { value: '90days', label: 'Next 90 days' },
  { value: '6months', label: 'Next 6 months' },
  { value: '1year', label: 'Next year' },
];

type TimeframeDialogType = 'importantDates' | 'summarizeEvents' | 'summarizeTasks' | null;

export default function AICoachStartPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const userIdRef = useRef<string | null>(userId);
  const [, setLocation] = useLocation();
  const [selectedSources, setSelectedSources] = useState<Set<DataSourceType>>(new Set());
  const [selectedConstraints, setSelectedConstraints] = useState<Record<DataSourceType, Set<string>>>({
    goals: new Set(),
    affirmations: new Set(),
    gratefuls: new Set(),
    habits: new Set(),
    tasks: new Set(),
    journal: new Set(),
    calendar: new Set(),
    importantDates: new Set(),
  });
  const [expandedSources, setExpandedSources] = useState<Set<DataSourceType>>(new Set());
  const [viewingAnalysis, setViewingAnalysis] = useState<AiCoachAnalysis | null>(null);
  const [showTimeframeDialog, setShowTimeframeDialog] = useState<TimeframeDialogType>(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeType>('30days');
  
  // New summary options with their own timeframes
  const [summarizeEvents, setSummarizeEvents] = useState(false);
  const [summarizeTasks, setSummarizeTasks] = useState(false);
  const [eventsTimeframe, setEventsTimeframe] = useState<TimeframeType>('30days');
  const [tasksTimeframe, setTasksTimeframe] = useState<TimeframeType>('30days');
  const [pendingTimeframeDialogs, setPendingTimeframeDialogs] = useState<TimeframeDialogType[]>([]);

  // Habit Time Blocking state
  const [showHabitAnalysis, setShowHabitAnalysis] = useState(false);
  const [habitAnalysisResults, setHabitAnalysisResults] = useState<HabitAnalysis[]>([]);
  const [analyzingHabits, setAnalyzingHabits] = useState(false);
  const [selectedHabitsForTimeBlock, setSelectedHabitsForTimeBlock] = useState<Set<string>>(new Set());
  const [showTimeBlockConfig, setShowTimeBlockConfig] = useState(false);
  const [currentTimeBlockHabit, setCurrentTimeBlockHabit] = useState<HabitAnalysis | null>(null);
  const [timeBlockConfigs, setTimeBlockConfigs] = useState<TimeBlockConfig[]>([]);
  const [showTimeBlockSummary, setShowTimeBlockSummary] = useState(false);
  const [showTimeBlockConfirmation, setShowTimeBlockConfirmation] = useState(false);
  const [creatingTimeBlocks, setCreatingTimeBlocks] = useState(false);
  
  // Time block form state
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [selectedDuration, setSelectedDuration] = useState(30);

  // Settings tab state
  const [settings, setSettings] = useState<AICoachSettings>(defaultSettings);
  const [activeTab, setActiveTab] = useState("coach");

  const handleSettingsToggle = (key: keyof AICoachSettings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Keep ref in sync so callbacks see the latest userId.
  useEffect(() => {
    userIdRef.current = userId;
    try {
      const saved = localStorage.getItem(userLsKey('ai-coach-settings', userId));
      if (saved) setSettings(JSON.parse(saved));
    } catch {}
  }, [userId]);

  const handleSaveSettings = () => {
    localStorage.setItem(userLsKey('ai-coach-settings', userIdRef.current), JSON.stringify(settings));
    toast({
      title: "Settings saved",
      description: "Your AI Coach settings have been updated.",
    });
  };

  const daysOfWeek = [
    { value: 'monday', label: 'Mon' },
    { value: 'tuesday', label: 'Tue' },
    { value: 'wednesday', label: 'Wed' },
    { value: 'thursday', label: 'Thu' },
    { value: 'friday', label: 'Fri' },
    { value: 'saturday', label: 'Sat' },
    { value: 'sunday', label: 'Sun' },
  ];

  const { data: analyses = [], isLoading: analysesLoading } = useQuery<AiCoachAnalysis[]>({
    queryKey: ['/api/ai-coach/analyses'],
  });

  const createAnalysisMutation = useMutation({
    mutationFn: async (data: { title: string; selectedSources: string[]; selectedConstraints: Record<string, string[]>; metadata?: Record<string, any> }) => {
      const response = await apiRequest('POST', '/api/ai-coach/analyses', data);
      return await response.json();
    },
    onSuccess: (analysis: AiCoachAnalysis) => {
      runAnalysisMutation.mutate(analysis.id);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create analysis",
        variant: "destructive",
      });
    },
  });

  const runAnalysisMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest('POST', `/api/ai-coach/analyses/${id}/run`);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/ai-coach/analyses'] });
      toast({
        title: "Analysis Complete",
        description: "Your AI analysis is ready to view.",
      });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/ai-coach/analyses'] });
      toast({
        title: "Error",
        description: "Failed to run analysis",
        variant: "destructive",
      });
    },
  });

  const deleteAnalysisMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/ai-coach/analyses/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/ai-coach/analyses'] });
      toast({
        title: "Deleted",
        description: "Analysis has been removed.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete analysis",
        variant: "destructive",
      });
    },
  });

  const toggleSource = (source: DataSourceType) => {
    const newSources = new Set(selectedSources);
    if (newSources.has(source)) {
      newSources.delete(source);
      setSelectedConstraints(prev => ({ ...prev, [source]: new Set() }));
    } else {
      newSources.add(source);
      const allConstraints = dataSourceConfigs[source].constraints.map(c => c.key);
      setSelectedConstraints(prev => ({ ...prev, [source]: new Set(allConstraints) }));
    }
    setSelectedSources(newSources);
  };

  const toggleConstraint = (source: DataSourceType, constraint: string) => {
    const newConstraints = new Set(selectedConstraints[source]);
    if (newConstraints.has(constraint)) {
      newConstraints.delete(constraint);
    } else {
      newConstraints.add(constraint);
    }
    setSelectedConstraints(prev => ({ ...prev, [source]: newConstraints }));
  };

  const toggleExpandSource = (source: DataSourceType) => {
    const newExpanded = new Set(expandedSources);
    if (newExpanded.has(source)) {
      newExpanded.delete(source);
    } else {
      newExpanded.add(source);
    }
    setExpandedSources(newExpanded);
  };

  const checkAll = () => {
    const allSources = new Set(allDataSources);
    setSelectedSources(allSources);
    const allConstraints: Record<DataSourceType, Set<string>> = {} as any;
    allDataSources.forEach(source => {
      allConstraints[source] = new Set(dataSourceConfigs[source].constraints.map(c => c.key));
    });
    setSelectedConstraints(allConstraints);
  };

  const uncheckAll = () => {
    setSelectedSources(new Set());
    setSelectedConstraints({
      goals: new Set(),
      affirmations: new Set(),
      gratefuls: new Set(),
      habits: new Set(),
      tasks: new Set(),
      journal: new Set(),
      calendar: new Set(),
      importantDates: new Set(),
    });
  };

  const startAnalysis = () => {
    if (selectedSources.size === 0 && !summarizeEvents && !summarizeTasks) {
      toast({
        title: "No data sources selected",
        description: "Please select at least one data source or summary option to analyze.",
        variant: "destructive",
      });
      return;
    }

    // Collect all features that need timeframe dialogs
    const dialogsNeeded: TimeframeDialogType[] = [];
    if (selectedSources.has('importantDates')) {
      dialogsNeeded.push('importantDates');
    }
    if (summarizeEvents) {
      dialogsNeeded.push('summarizeEvents');
    }
    if (summarizeTasks) {
      dialogsNeeded.push('summarizeTasks');
    }

    if (dialogsNeeded.length > 0) {
      setPendingTimeframeDialogs(dialogsNeeded);
      setShowTimeframeDialog(dialogsNeeded[0]);
      return;
    }

    proceedWithAnalysis();
  };

  const getTimeframeDays = (timeframe: TimeframeType): number => {
    switch (timeframe) {
      case '30days': return 30;
      case '60days': return 60;
      case '90days': return 90;
      case '6months': return 180;
      case '1year': return 365;
      default: return 30;
    }
  };

  const handleTimeframeConfirm = () => {
    // Store the timeframe for the current dialog type
    if (showTimeframeDialog === 'importantDates') {
      // Already using selectedTimeframe for importantDates
    } else if (showTimeframeDialog === 'summarizeEvents') {
      setEventsTimeframe(selectedTimeframe);
    } else if (showTimeframeDialog === 'summarizeTasks') {
      setTasksTimeframe(selectedTimeframe);
    }

    // Move to next dialog or proceed
    const currentIndex = pendingTimeframeDialogs.indexOf(showTimeframeDialog!);
    if (currentIndex < pendingTimeframeDialogs.length - 1) {
      setSelectedTimeframe('30days'); // Reset for next dialog
      setShowTimeframeDialog(pendingTimeframeDialogs[currentIndex + 1]);
    } else {
      // All dialogs completed, proceed with analysis
      proceedWithAnalysis();
    }
  };

  const proceedWithAnalysis = () => {
    const sources = Array.from(selectedSources);
    const constraints: Record<string, string[]> = {};
    sources.forEach(source => {
      constraints[source] = Array.from(selectedConstraints[source]);
    });

    const metadata: Record<string, any> = {};
    if (selectedSources.has('importantDates')) {
      metadata.importantDatesTimeframe = getTimeframeDays(selectedTimeframe);
    }
    if (summarizeEvents) {
      metadata.summarizeEvents = true;
      metadata.eventsTimeframe = getTimeframeDays(eventsTimeframe);
    }
    if (summarizeTasks) {
      metadata.summarizeTasks = true;
      metadata.tasksTimeframe = getTimeframeDays(tasksTimeframe);
    }

    const title = `Analysis - ${format(new Date(), 'MMM d, yyyy h:mm a')}`;
    createAnalysisMutation.mutate({ title, selectedSources: sources, selectedConstraints: constraints, metadata });
    setShowTimeframeDialog(null);
    setPendingTimeframeDialogs([]);
  };

  const isRunning = createAnalysisMutation.isPending || runAnalysisMutation.isPending;

  // Habit Analysis & Time Blocking Functions
  const analyzeHabits = async () => {
    setAnalyzingHabits(true);
    try {
      const response = await apiRequest('GET', '/api/ai-coach/analyze-habits');
      const results: HabitAnalysis[] = await response.json();
      setHabitAnalysisResults(results);
      setShowHabitAnalysis(true);
      setSelectedHabitsForTimeBlock(new Set());
      setTimeBlockConfigs([]);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to analyze habits",
        variant: "destructive",
      });
    } finally {
      setAnalyzingHabits(false);
    }
  };

  const toggleHabitForTimeBlock = (habitId: string) => {
    const hasConfig = timeBlockConfigs.some(c => c.habitId === habitId);
    
    if (hasConfig) {
      // Remove config when unchecking
      setTimeBlockConfigs(prev => prev.filter(c => c.habitId !== habitId));
    } else {
      // Open config dialog when checking (config will be added on save)
      const habit = habitAnalysisResults.find(h => h.id === habitId);
      if (habit) {
        setCurrentTimeBlockHabit(habit);
        setSelectedDays([]);
        setSelectedTime('09:00');
        setSelectedDuration(30);
        setShowTimeBlockConfig(true);
      }
    }
  };

  const handleTimeBlockConfigSave = () => {
    if (!currentTimeBlockHabit || selectedDays.length === 0) {
      toast({
        title: "Please select at least one day",
        variant: "destructive",
      });
      return;
    }

    const newConfig: TimeBlockConfig = {
      habitId: currentTimeBlockHabit.id,
      habitName: currentTimeBlockHabit.name,
      days: selectedDays,
      time: selectedTime,
      duration: selectedDuration,
    };

    setTimeBlockConfigs(prev => [...prev.filter(c => c.habitId !== currentTimeBlockHabit.id), newConfig]);
    setShowTimeBlockConfig(false);
    setCurrentTimeBlockHabit(null);
  };

  const generateTimeBlockPreview = (): TimeBlockPreview[] => {
    const today = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    
    return timeBlockConfigs.map(config => {
      const events: TimeBlockPreview['events'] = [];
      
      for (let i = 0; i < 14; i++) {
        const date = addDays(today, i);
        const dayName = dayNames[date.getDay()];
        
        if (config.days.includes(dayName)) {
          events.push({
            date: format(date, 'EEE, MMM d'),
            time: config.time,
            duration: config.duration,
          });
        }
      }
      
      return {
        habitName: config.habitName,
        events,
      };
    });
  };

  const createTimeBlockEvents = async () => {
    setCreatingTimeBlocks(true);
    try {
      const response = await apiRequest('POST', '/api/ai-coach/create-time-blocks', {
        configs: timeBlockConfigs,
      });
      
      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Time Blocks Created",
          description: `Successfully created ${result.eventsCreated} calendar events for your habits.`,
        });
        queryClient.invalidateQueries({ queryKey: ['/api/calendar/events'] });
        setShowTimeBlockConfirmation(false);
        setShowTimeBlockSummary(false);
        setShowHabitAnalysis(false);
        setTimeBlockConfigs([]);
        setSelectedHabitsForTimeBlock(new Set());
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create time blocks",
        variant: "destructive",
      });
    } finally {
      setCreatingTimeBlocks(false);
    }
  };

  const openTimeBlockSummary = () => {
    if (timeBlockConfigs.length === 0) {
      toast({
        title: "No habits configured",
        description: "Please select habits and configure their time blocks first.",
        variant: "destructive",
      });
      return;
    }
    setShowTimeBlockSummary(true);
  };

  const handleActionLinkClick = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const actionLink = target.closest('.action-link') as HTMLAnchorElement;
    if (actionLink) {
      e.preventDefault();
      const href = actionLink.getAttribute('href');
      if (href) {
        setViewingAnalysis(null);
        setLocation(href);
      }
    }
  }, [setLocation]);

  const exportToPdf = (analysis: AiCoachAnalysis) => {
    if (!analysis.summaryHtml) return;
    
    const safeTitle = DOMPurify.sanitize(analysis.title, { ALLOWED_TAGS: [] });
    const safeHtml = DOMPurify.sanitize(analysis.summaryHtml);
    const safeDate = format(new Date(analysis.createdAt), 'MMMM d, yyyy h:mm a');

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${safeTitle}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
              h1 { color: #1a1a1a; border-bottom: 2px solid #4f46e5; padding-bottom: 10px; }
              h2 { color: #4f46e5; margin-top: 24px; }
              h3 { color: #333; }
              ul, ol { margin: 10px 0; padding-left: 24px; }
              li { margin: 6px 0; }
              p { line-height: 1.6; }
              .date { color: #666; font-size: 14px; margin-bottom: 20px; }
            </style>
          </head>
          <body>
            <h1>${safeTitle}</h1>
            <p class="date">Generated: ${safeDate}</p>
            ${safeHtml}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'running':
        return <Badge className="bg-blue-500"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Running</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  return (
    <div className="w-full max-w-full py-6 md:py-8 px-4 md:px-6">
      <div className="flex items-center gap-3 mb-6">
        <Bot className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-start-coach-title">AI Coach</h1>
          <p className="text-muted-foreground">Select AI coaching options and run analysis</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="grid w-full grid-cols-3 max-w-[450px]">
          <TabsTrigger value="coach" data-testid="tab-coach">
            <Play className="w-4 h-4 mr-2" />
            Coach
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="reporting" data-testid="tab-reporting">
            <BarChart3 className="w-4 h-4 mr-2" />
            Reporting
          </TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Data Sources</CardTitle>
              <CardDescription>
                Toggle which areas of your account the AI Coach should include when providing personalized coaching and insights.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {settingsItems.map((item) => {
                const Icon = item.icon;
                const isEnabled = settings[item.key];
                return (
                  <div key={item.key} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-muted rounded-md flex items-center justify-center">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div>
                        <Label htmlFor={item.key} className="text-base font-medium cursor-pointer">
                          {item.label}
                        </Label>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        id={item.key}
                        checked={isEnabled}
                        onCheckedChange={() => handleSettingsToggle(item.key)}
                        data-testid={`switch-${item.key}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={!isEnabled}
                        onClick={() => setLocation(item.constraintsRoute)}
                        className={`h-8 w-8 ${!isEnabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-muted'}`}
                        data-testid={`button-constraints-${item.key}`}
                      >
                        <Settings className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    </div>
                  </div>
                );
              })}

              <div className="pt-4 border-t">
                <Button onClick={handleSaveSettings} className="w-full" data-testid="button-save-ai-coach-settings">
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reporting" className="mt-6">
          <ReportingContent />
        </TabsContent>

        <TabsContent value="coach" className="mt-6">
      <Card className="mb-8">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>AI Analysis Options</CardTitle>
              <CardDescription>Select the data sources and insights you want the AI to analyze</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={checkAll} data-testid="button-check-all">
                Check All
              </Button>
              <Button variant="outline" size="sm" onClick={uncheckAll} data-testid="button-uncheck-all">
                Uncheck All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {allDataSources.map((source) => {
            const config = dataSourceConfigs[source];
            const Icon = config.icon;
            const isSelected = selectedSources.has(source);
            const isExpanded = expandedSources.has(source);
            const selectedCount = selectedConstraints[source].size;
            const totalCount = config.constraints.length;

            return (
              <div key={source} className="border rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id={`source-${source}`}
                    checked={isSelected}
                    onCheckedChange={() => toggleSource(source)}
                    data-testid={`checkbox-source-${source}`}
                  />
                  <Icon className="w-5 h-5 text-muted-foreground" />
                  <Label htmlFor={`source-${source}`} className="flex-1 cursor-pointer font-medium">
                    {config.label}
                  </Label>
                  {isSelected && (
                    <span className="text-sm text-muted-foreground">
                      {selectedCount}/{totalCount} insights
                    </span>
                  )}
                  {isSelected && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpandSource(source)}
                      data-testid={`button-expand-${source}`}
                    >
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </Button>
                  )}
                </div>

                {isSelected && isExpanded && (
                  <div className="mt-3 ml-8 pl-4 border-l-2 space-y-2">
                    {config.constraints.map((constraint) => {
                      const ConstraintIcon = constraint.icon;
                      return (
                        <div key={constraint.key} className="flex items-center gap-3">
                          <Checkbox
                            id={`constraint-${source}-${constraint.key}`}
                            checked={selectedConstraints[source].has(constraint.key)}
                            onCheckedChange={() => toggleConstraint(source, constraint.key)}
                            data-testid={`checkbox-constraint-${source}-${constraint.key}`}
                          />
                          <ConstraintIcon className="w-4 h-4 text-muted-foreground" />
                          <Label htmlFor={`constraint-${source}-${constraint.key}`} className="cursor-pointer text-sm">
                            {constraint.label}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <Separator className="my-6" />

          {/* Summary Options */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">Additional Summary Options</h3>
            <div className="border rounded-lg p-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="summarize-events"
                  checked={summarizeEvents}
                  onCheckedChange={(checked) => setSummarizeEvents(checked === true)}
                  data-testid="checkbox-summarize-events"
                />
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <Label htmlFor="summarize-events" className="flex-1 cursor-pointer font-medium">
                  Summarize Upcoming Events
                </Label>
                {summarizeEvents && (
                  <span className="text-sm text-muted-foreground">
                    {timeframeOptions.find(t => t.value === eventsTimeframe)?.label || 'Select timeframe'}
                  </span>
                )}
              </div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="summarize-tasks"
                  checked={summarizeTasks}
                  onCheckedChange={(checked) => setSummarizeTasks(checked === true)}
                  data-testid="checkbox-summarize-tasks"
                />
                <ListTodo className="w-5 h-5 text-muted-foreground" />
                <Label htmlFor="summarize-tasks" className="flex-1 cursor-pointer font-medium">
                  Summarize Upcoming Tasks
                </Label>
                {summarizeTasks && (
                  <span className="text-sm text-muted-foreground">
                    {timeframeOptions.find(t => t.value === tasksTimeframe)?.label || 'Select timeframe'}
                  </span>
                )}
              </div>
            </div>
          </div>

          <Separator className="my-6" />

          {/* Habit Time Blocking */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">Habit Time Blocking</h3>
            <div className="border rounded-lg p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/20 dark:to-indigo-950/20">
              <div className="flex items-center gap-3 mb-3">
                <CalendarPlus className="w-6 h-6 text-purple-600" />
                <div>
                  <h4 className="font-medium">Analyze Habits & Schedule Time Blocks</h4>
                  <p className="text-sm text-muted-foreground">
                    Identify habits on track vs. needing improvement, then create calendar time blocks
                  </p>
                </div>
              </div>
              <Button
                onClick={analyzeHabits}
                disabled={analyzingHabits}
                className="w-full bg-purple-600 hover:bg-purple-700"
                data-testid="button-analyze-habits"
              >
                {analyzingHabits ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing Habits...
                  </>
                ) : (
                  <>
                    <Brain className="w-4 h-4 mr-2" />
                    Analyze Habits & Time Blocking
                  </>
                )}
              </Button>
            </div>
          </div>

          <Separator className="my-6" />

          <div className="flex justify-center">
            <Button
              size="lg"
              onClick={startAnalysis}
              disabled={isRunning || (selectedSources.size === 0 && !summarizeEvents && !summarizeTasks)}
              className="w-full max-w-md"
              data-testid="button-start-analysis"
            >
              {isRunning ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Running Analysis...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5 mr-2" />
                  Start AI Analysis
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Past Analyses
          </CardTitle>
          <CardDescription>View and export your previous AI coaching analyses</CardDescription>
        </CardHeader>
        <CardContent>
          {analysesLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : analyses.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No analyses yet. Run your first AI analysis above!</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sources</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analyses.map((analysis) => {
                  const sources = (analysis.selectedSources as string[]) || [];
                  return (
                    <TableRow
                      key={analysis.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => analysis.status === 'completed' && setViewingAnalysis(analysis)}
                      data-testid={`row-analysis-${analysis.id}`}
                    >
                      <TableCell className="font-medium">{analysis.title}</TableCell>
                      <TableCell>{format(new Date(analysis.createdAt), 'MMM d, yyyy h:mm a')}</TableCell>
                      <TableCell>{getStatusBadge(analysis.status)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {sources.slice(0, 3).map((source) => {
                            const config = dataSourceConfigs[source as DataSourceType];
                            if (!config) return null;
                            const SourceIcon = config.icon;
                            return (
                              <Badge key={source} variant="outline" className="text-xs">
                                <SourceIcon className="w-3 h-3 mr-1" />
                                {config.label}
                              </Badge>
                            );
                          })}
                          {sources.length > 3 && (
                            <Badge variant="outline" className="text-xs">+{sources.length - 3}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {analysis.status === 'completed' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setViewingAnalysis(analysis)}
                                data-testid={`button-view-${analysis.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => exportToPdf(analysis)}
                                data-testid={`button-export-${analysis.id}`}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteAnalysisMutation.mutate(analysis.id)}
                            data-testid={`button-delete-${analysis.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!viewingAnalysis} onOpenChange={() => setViewingAnalysis(null)}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewingAnalysis?.title}</DialogTitle>
            <DialogDescription>
              Generated on {viewingAnalysis && format(new Date(viewingAnalysis.createdAt), 'MMMM d, yyyy h:mm a')}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            <div
              className="prose dark:prose-invert max-w-none analysis-content"
              onClick={handleActionLinkClick}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewingAnalysis?.summaryHtml || '') }}
            />
          </ScrollArea>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setViewingAnalysis(null)}>Close</Button>
            {viewingAnalysis && (
              <Button onClick={() => exportToPdf(viewingAnalysis)} data-testid="button-export-dialog">
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showTimeframeDialog} onOpenChange={() => { setShowTimeframeDialog(null); setPendingTimeframeDialogs([]); }}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="dialog-timeframe">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {showTimeframeDialog === 'importantDates' && (
                <>
                  <Star className="w-5 h-5 text-yellow-500" />
                  Important Dates Analysis
                </>
              )}
              {showTimeframeDialog === 'summarizeEvents' && (
                <>
                  <Calendar className="w-5 h-5 text-blue-500" />
                  Upcoming Events Summary
                </>
              )}
              {showTimeframeDialog === 'summarizeTasks' && (
                <>
                  <ListTodo className="w-5 h-5 text-green-500" />
                  Upcoming Tasks Summary
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {showTimeframeDialog === 'importantDates' && 'Select time range for important dates analysis'}
              {showTimeframeDialog === 'summarizeEvents' && 'Select time range for event summary'}
              {showTimeframeDialog === 'summarizeTasks' && 'Select time range for task summary'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <RadioGroup
              value={selectedTimeframe}
              onValueChange={(value) => setSelectedTimeframe(value as TimeframeType)}
              className="space-y-3"
            >
              {timeframeOptions.map((option) => (
                <div key={option.value} className="flex items-center space-x-3">
                  <RadioGroupItem 
                    value={option.value} 
                    id={`timeframe-${option.value}`}
                    data-testid={`radio-timeframe-${option.value}`}
                  />
                  <Label htmlFor={`timeframe-${option.value}`} className="cursor-pointer font-normal">
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setShowTimeframeDialog(null); setPendingTimeframeDialogs([]); }}
              data-testid="button-cancel-timeframe"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleTimeframeConfirm}
              disabled={isRunning}
              data-testid="button-confirm-timeframe"
            >
              {pendingTimeframeDialogs.indexOf(showTimeframeDialog!) < pendingTimeframeDialogs.length - 1 ? (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              ) : isRunning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Start Analysis
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Habit Analysis Results Dialog */}
      <Dialog open={showHabitAnalysis} onOpenChange={setShowHabitAnalysis}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="dialog-habit-analysis">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-600" />
              Habit Analysis & Time Blocking
            </DialogTitle>
            <DialogDescription>
              Review your habits and select which ones need time blocks on your calendar
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[50vh] pr-4">
            {habitAnalysisResults.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No habits found. Create some habits first!</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* On Track Habits */}
                <div>
                  <h3 className="font-medium text-green-600 flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5" />
                    Habits On Track ({habitAnalysisResults.filter(h => h.status === 'on-track').length})
                  </h3>
                  <div className="space-y-2">
                    {habitAnalysisResults.filter(h => h.status === 'on-track').map(habit => (
                      <div key={habit.id} className="flex items-center gap-3 p-3 border rounded-lg bg-green-50 dark:bg-green-950/20">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <div className="flex-1">
                          <p className="font-medium">{habit.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {habit.frequency} • {habit.streak} day streak • {habit.completionRate}% completion
                          </p>
                        </div>
                        <Badge className="bg-green-500">On Track</Badge>
                      </div>
                    ))}
                    {habitAnalysisResults.filter(h => h.status === 'on-track').length === 0 && (
                      <p className="text-sm text-muted-foreground italic">No habits currently on track</p>
                    )}
                  </div>
                </div>

                {/* Needs Improvement Habits */}
                <div>
                  <h3 className="font-medium text-amber-600 flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5" />
                    Needs Improvement ({habitAnalysisResults.filter(h => h.status === 'needs-improvement').length})
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Check the box next to habits you'd like to schedule time blocks for:
                  </p>
                  <div className="space-y-2">
                    {habitAnalysisResults.filter(h => h.status === 'needs-improvement').map(habit => {
                      const hasConfig = timeBlockConfigs.some(c => c.habitId === habit.id);
                      return (
                        <div 
                          key={habit.id} 
                          className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                            hasConfig ? 'bg-purple-50 dark:bg-purple-950/20 border-purple-300' : 'bg-amber-50 dark:bg-amber-950/20'
                          }`}
                        >
                          <Checkbox
                            id={`habit-timeblock-${habit.id}`}
                            checked={hasConfig}
                            onCheckedChange={() => toggleHabitForTimeBlock(habit.id)}
                            data-testid={`checkbox-habit-${habit.id}`}
                          />
                          <div className="flex-1">
                            <p className="font-medium">{habit.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {habit.frequency} • {habit.streak} day streak • {habit.completionRate}% completion
                            </p>
                            <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">{habit.recommendation}</p>
                            {hasConfig && (
                              <div className="mt-2 flex items-center gap-2">
                                <Badge variant="outline" className="text-purple-600">
                                  <Timer className="w-3 h-3 mr-1" />
                                  {timeBlockConfigs.find(c => c.habitId === habit.id)?.days.join(', ')} at {timeBlockConfigs.find(c => c.habitId === habit.id)?.time}
                                </Badge>
                              </div>
                            )}
                          </div>
                          <Badge variant="outline" className="text-amber-600">Needs Work</Badge>
                        </div>
                      );
                    })}
                    {habitAnalysisResults.filter(h => h.status === 'needs-improvement').length === 0 && (
                      <p className="text-sm text-muted-foreground italic">All habits are on track!</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </ScrollArea>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowHabitAnalysis(false)}>
              Cancel
            </Button>
            <Button 
              onClick={openTimeBlockSummary}
              disabled={timeBlockConfigs.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="button-create-time-blocks"
            >
              <CalendarPlus className="w-4 h-4 mr-2" />
              Create Time Blocks ({timeBlockConfigs.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time Block Configuration Dialog (small) */}
      <Dialog open={showTimeBlockConfig} onOpenChange={setShowTimeBlockConfig}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="dialog-timeblock-config">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Timer className="w-5 h-5 text-purple-600" />
              Configure Time Block
            </DialogTitle>
            <DialogDescription>
              Set schedule for: <strong>{currentTimeBlockHabit?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Select Days</Label>
              <div className="flex flex-wrap gap-2">
                {daysOfWeek.map(day => (
                  <Button
                    key={day.value}
                    variant={selectedDays.includes(day.value) ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (selectedDays.includes(day.value)) {
                        setSelectedDays(prev => prev.filter(d => d !== day.value));
                      } else {
                        setSelectedDays(prev => [...prev, day.value]);
                      }
                    }}
                    data-testid={`button-day-${day.value}`}
                  >
                    {day.label}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="time-input" className="text-sm font-medium mb-2 block">Time of Day</Label>
              <Input
                id="time-input"
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="w-full"
                data-testid="input-time"
              />
            </div>
            <div>
              <Label htmlFor="duration-select" className="text-sm font-medium mb-2 block">Duration (minutes)</Label>
              <Select value={String(selectedDuration)} onValueChange={(v) => setSelectedDuration(Number(v))}>
                <SelectTrigger data-testid="select-duration">
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowTimeBlockConfig(false); setCurrentTimeBlockHabit(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleTimeBlockConfigSave}
              disabled={selectedDays.length === 0}
              data-testid="button-save-timeblock"
            >
              <Check className="w-4 h-4 mr-2" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time Block Summary Dialog */}
      <Dialog open={showTimeBlockSummary} onOpenChange={setShowTimeBlockSummary}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="dialog-timeblock-summary">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-purple-600" />
              Time Block Summary
            </DialogTitle>
            <DialogDescription>
              Review the calendar events that will be created (next 2 weeks)
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[40vh] pr-4">
            <div className="space-y-4">
              {generateTimeBlockPreview().map((preview, idx) => (
                <div key={idx} className="border rounded-lg p-4">
                  <h4 className="font-medium text-purple-600 mb-3">{preview.habitName}</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {preview.events.map((event, eventIdx) => (
                      <div key={eventIdx} className="flex items-center gap-2 text-sm bg-purple-50 dark:bg-purple-950/20 p-2 rounded">
                        <Calendar className="w-4 h-4 text-purple-600" />
                        <span>{event.date}</span>
                        <span className="text-muted-foreground">at {event.time}</span>
                        <span className="text-muted-foreground">({event.duration}min)</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowTimeBlockSummary(false)}>
              Back
            </Button>
            <Button 
              onClick={() => setShowTimeBlockConfirmation(true)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="button-confirm-summary"
            >
              <Check className="w-4 h-4 mr-2" />
              Confirm & Create Events
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Final Confirmation Dialog */}
      <Dialog open={showTimeBlockConfirmation} onOpenChange={setShowTimeBlockConfirmation}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="dialog-timeblock-confirmation">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Create Habit Time Blocks?
            </DialogTitle>
            <DialogDescription>
              This will create recurring calendar events for your selected habits.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-purple-50 dark:bg-purple-950/20 p-4 rounded-lg">
              <p className="text-sm mb-2">You're about to create:</p>
              <ul className="text-sm space-y-1">
                {timeBlockConfigs.map(config => (
                  <li key={config.habitId} className="flex items-center gap-2">
                    <CheckSquare className="w-4 h-4 text-purple-600" />
                    <span className="font-medium">{config.habitName}</span>
                    <span className="text-muted-foreground">
                      ({config.days.length} days/week)
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-muted-foreground mt-3">
                Total: {generateTimeBlockPreview().reduce((acc, p) => acc + p.events.length, 0)} events over 2 weeks
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTimeBlockConfirmation(false)}>
              Cancel
            </Button>
            <Button 
              onClick={createTimeBlockEvents}
              disabled={creatingTimeBlocks}
              className="bg-green-600 hover:bg-green-700"
              data-testid="button-final-confirm"
            >
              {creatingTimeBlocks ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Create Time Blocks
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}
